import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';

import ResourcesAPI from '../../api/ResourcesAPI';
import * as types from './ResourcesActionTypes';
import Config from '../../../config.json';
import { ERROR } from '../central/CentralActionTypes';

export function updateLoadingState(type) {
  return {
    type,
  };
}

export function updateLoadingStateWithPathCheck(type, resourcePath) {
  return {
    type,
    resourcePath,
  };
}

export function dataRetrievalFailure(error) {
  return {
    type: ERROR, error,
  };
}

export function clearErrors() {
  return {
    type: ERROR,
  };
}

/**
 * Forgot password actions:
 *    get recovery token
 *    verify token
 *    reset password
 */
export function getRecoveryTokenSuccess(returnedAdmin) {
  return {
    type: types.GET_RECOVERY_TOKEN_SUCCESS,
    returnedAdmin,
  };
}

export function getRecoveryToken(adminEmail) {
  return dispatch => ResourcesAPI.addResource(Config.general.apiKey, adminEmail, ['admins', 'forgot-password'])
    .then(res => dispatch(getRecoveryTokenSuccess(res)))
    .catch(err => dispatch(dataRetrievalFailure(err)));
}

export function verifyRecoveryTokenSuccess(returnedAdmin) {
  return {
    type: types.VERIFY_RECOVERY_TOKEN_SUCCESS,
    returnedAdmin,
  };
}

export function verifyRecoveryToken(adminObj) {
  return dispatch => ResourcesAPI.addResource(Config.general.apiKey, adminObj, ['admins', 'verify-token'])
    .then(res => dispatch(getRecoveryTokenSuccess(res)))
    .catch(err => dispatch(dataRetrievalFailure(err)));
}

export function confirmResetPasswordSuccess(returnedAdmin) {
  return {
    type: types.CONFIRM_RESET_PASSWORD_SUCCESS,
    returnedAdmin,
  };
}

export function confirmResetPassword(adminObj) {
  return dispatch => ResourcesAPI.updateResource(Config.general.apiKey, adminObj, ['admins', 'reset-password'])
    .then(res => dispatch(confirmResetPasswordSuccess(res.data)))
    .catch(err => dispatch(dataRetrievalFailure(err)));
}


/*
 * Resource Actions
 * ...resourcePath is the full path to the resource. It can include multiple nested resources such as
 * ('categories') -> all categories
 * ('categories',1) -> category with id 1
 * ('categories',1,'products') -> all products of category with id 1
 * ('categories',1,'products',2) -> product with id 2 of category with id 1
 * ...
 */

export function deleteResource(apiToken, ...resourcePath) {
  updateLoadingState(types.DELETE_RESOURCE_REQUESTED);

  return dispatch => ResourcesAPI.deleteResource(apiToken, resourcePath)
    .then(() => dispatch(deleteResourceSuccess(resourcePath)),
      error => dispatch(dataRetrievalFailure(error)));
}

export function deleteResourceSuccess(resourcePath) {
  return {
    type: types.DELETE_RESOURCE_SUCCESS, resourcePath,
  };
}

export function addResource(apiToken, resource, ...resourcePath) {
  return (dispatch) => {
    dispatch(updateLoadingState(types.ADD_RESOURCE_REQUESTED));
    return ResourcesAPI.addResource(apiToken, resource, resourcePath)
      .then(
        response => dispatch(addResourceSuccess(response.data, resourcePath)),
        error => dispatch(dataRetrievalFailure(error)),
      );
  };
}

export function duplicateResource(apiToken, resourcePath) {
  return (dispatch) => {
    dispatch(updateLoadingState(types.ADD_RESOURCE_REQUESTED));
    return ResourcesAPI.duplicateResource(apiToken, resourcePath)
      .then(
        response => dispatch(addResourceSuccess(response.data, resourcePath)),
        error => dispatch(dataRetrievalFailure(error)),
      );
  };
}

export function addResourceSuccess(response, resourcePath) {
  return {
    type: types.ADD_RESOURCE_SUCCESS,
    response,
    resourcePath,
  };
}

export function uploadResource(apiToken, resource, ...resourcePath) {
  return (dispatch) => {
    dispatch(updateLoadingState(types.UPLOAD_RESOURCE_REQUESTED));
    return ResourcesAPI.uploadResource(apiToken, resource, resourcePath)
      .then(response => dispatch(uploadResourceSuccess(
        response, resourcePath,
      )), error => dispatch(dataRetrievalFailure(error)));
  };
}

export function uploadResourceSuccess(response, resourcePath) {
  return {
    type: types.UPLOAD_RESOURCE_SUCCESS,
    response,
    resourcePath,
  };
}

export function getDownloadResourcesSuccess(response, resourcePath) {
  return {
    type: types.GET_DOWNLOAD_RESOURCES_SUCCESS,
    response,
    resourcePath,
  };
}
export function downloadResource(apiToken, ...resourcePath) {
  return (dispatch) => {
    dispatch(updateLoadingState(types.GET_DOWNLOAD_RESOURCE_REQUESTED));
    return ResourcesAPI.downloadResource(apiToken, resourcePath)
      .then(response => dispatch(getDownloadResourcesSuccess(
        response, resourcePath,
      )), error => dispatch(dataRetrievalFailure(error)));
  };
}

export function updateResource(apiToken, newResource, ...resourcePath) {
  return (dispatch) => {
    dispatch(updateLoadingState(types.UPDATE_RESOURCE_REQUESTED));
    return ResourcesAPI.updateResource(apiToken, newResource, resourcePath)
      .then(
        response => dispatch(updateResourceSuccess(response.data, resourcePath)),
        error => dispatch(dataRetrievalFailure(error)),
      );
  };
}

export function updateResourceSuccess(response, resourcePath) {
  return {
    type: types.UPDATE_RESOURCE_SUCCESS,
    response,
    resourcePath,
  };
}

export function getAllResourcesSuccess(response, resourcePath) {
  return {
    type: types.GET_ALL_RESOURCES_SUCCESS,
    response,
    resourcePath,
  };
}

export function getAllResources(apiToken, ...resourcePath) {
  return (dispatch) => {
    dispatch(updateLoadingState(types.GET_ALL_RESOURCES_REQUESTED));
    return ResourcesAPI.getAllResources(apiToken, resourcePath)
      .then(response => dispatch(getAllResourcesSuccess(
        response.data, resourcePath,
      )),
      error => dispatch(dataRetrievalFailure(error)));
  };
}

export function getAllResourcesRecursive(apiToken, accumulator = [], ...resourcePath) {
  return async (dispatch) => {
    let response;
    try {
      response = await ResourcesAPI.getAllResources(apiToken, resourcePath);
    } catch (e) {
      dispatch(dataRetrievalFailure(e));
    }

    const start = get(response, 'data.start');
    const count = get(response, 'data.count');
    const totalSize = get(response, 'data.totalSize');
    let entityList;
    const alteredResponse = {};
    Object.entries(response.data).forEach(([key, value]) => {
      if (['start', 'count', 'totalSize'].includes(key)) {
        alteredResponse[key] = value;
      } else {
        entityList = value;
      }
    });

    const currentList = accumulator.concat(entityList);

    if (Number(start) + Number(count) > Number(totalSize)) {
      alteredResponse.entityList = currentList;
      return dispatch(getAllResourcesSuccess(alteredResponse, resourcePath));
    } else {
      const nextStart = start + count;
      const path = resourcePath[0];
      const [mainPath, oldQueryParam] = path.split('?');
      const oldSearchParamObj = new URLSearchParams(oldQueryParam);

      const newQueryParam = new URLSearchParams();
      newQueryParam.append('start', `${nextStart}`);
      newQueryParam.append('count', `${count}`);

      for (const [key, value] of oldSearchParamObj.entries()) {
        if (!['start', 'count'].includes(key)) newQueryParam.append(key, value);
      }
      const nextUrl = `${mainPath}?${newQueryParam.toString()}`;
      return dispatch(getAllResourcesRecursive(apiToken, currentList, nextUrl));
    }
  };
}

export function filterProducts(category) {
  return {
    type: types.FILTER_PRODUCTS, category,
  };
}

export function getHomeAnalytics(apiToken, ...resourcePath) {
  return (dispatch) => {
    dispatch(updateLoadingStateWithPathCheck(types.GET_HOME_ANALYTICS_REQUESTED, resourcePath));
    return ResourcesAPI.getAllResources(apiToken, resourcePath)
      .then(response => dispatch(getAllResourcesSuccess(
        response.data, resourcePath,
      )),
      error => dispatch(dataRetrievalFailure(error)));
  };
}

export function getSearchedUsers(apiToken, ...resourcePath) {
  return (dispatch) => {
    dispatch(updateLoadingState(types.GET_SEARCHED_USERS_REQUESTED));
    return ResourcesAPI.getAllResources(apiToken, resourcePath)
      .then(response => dispatch(getSearchedUsersSuccess(response.data)),
        error => dispatch(dataRetrievalFailure(error)));
  };
}

export function getSearchedUsersSuccess(response) {
  return {
    type: types.GET_SEARCHED_USERS_SUCCESS,
    response,
  };
}

export function getRecent30daysOrders(apiToken, ...resourcePath) {
  return (dispatch) => {
    dispatch(updateLoadingState(types.GET_RECENT_ORDERS_REQUESTED));
    return ResourcesAPI.getAllResources(apiToken, resourcePath)
      .then(res => dispatch(getRecent30daysOrdersSuccess(res)))
      .catch(err => dispatch(dataRetrievalFailure(err)));
  };
}

export function getRecent30daysOrdersSuccess(recentOrders) {
  return {
    type: types.GET_RECENT_ORDERS_SUCCESS,
    recentOrders,
  };
}

export function sendPushNotificationSuccess(response) {
  return {
    type: types.SEND_PUSH_NOTIFICATION_SUCCESS,
    response,
  };
}

export function sendPushNotification(apiToken, message, ...resourcePath) {
  return (dispatch) => {
    dispatch(updateLoadingState(types.SEND_PUSH_NOTIFICATION));
    return ResourcesAPI.addResource(apiToken, message, resourcePath, true)
      .then(res => dispatch(sendPushNotificationSuccess(res)),
        error => dispatch(dataRetrievalFailure(error)));
  };
}

export function updatePageSettings(keyPath, value) {
  keyPath.unshift('pageSettings');
  return {
    type: types.UPDATE_PAGE_SETTINGS,
    keyPath,
    value,
  };
}

export function batchRequests(requestActionType, apiToken, requestPaths) {
  return async (dispatch) => {
    const numRequests = Array.isArray(requestPaths) && requestPaths.length;
    dispatch(addRequestsLeftToLoad(numRequests));
    const responses = requestPaths.map((path) => {
      try {
        return ResourcesAPI[requestActionType](apiToken, path);
      } catch (e) {
        dispatch(dataRetrievalFailure(e));
      }
    });
    const results = await Promise.allSettled(responses);
    dispatch(removeRequestsLeftToLoad(numRequests));
    const batchUpdateStoreAction = batchUpdateStoreActions[requestActionType];
    dispatch(batchUpdateStoreAction(results));
  };
}

export function addRequestsLeftToLoad(numRequests) {
  return {
    type: types.ADD_REQUESTS_LEFT_TO_LOAD,
    numRequests,
  };
}

export function removeRequestsLeftToLoad(numRequests) {
  return {
    type: types.REMOVE_REQUESTS_LEFT_TO_LOAD,
    numRequests,
  };
}

export function batchUpdateAfterGetAllResources(apiResults) {
  return {
    type: types.BATCH_UPDATE_AFTER_GET_ALL_RESOURCES,
    responses: apiResults,
  };
}

const batchUpdateStoreActions = {
  getAllResources: batchUpdateAfterGetAllResources,
};

function reprintOrderSuccess() {
  return {
    type: types.REPRINT_ORDER_SUCCESS,
  };
}

function reprintOrderFailure(error) {
  return {
    type: types.REPRINT_ORDER_FAILURE,
    error,
  };
}

export function reprintOrder(user, adminToken, ...resourcePath) {
  return (dispatch) => {
    dispatch(updateLoadingState(types.REPRINT_ORDER_REQUESTED));
    return ResourcesAPI.reprint(user, resourcePath, adminToken)
      .then(
        () => dispatch(reprintOrderSuccess()),
        error => dispatch(reprintOrderFailure(error)),
      );
  };
}

export function setModifiersOnOptions({
  apiToken,
  option,
  selectedCustomizations = [],
  requestsInfo,
  stayedSelectedsMap,
}) {
  return async (dispatch) => {
    dispatch(updateLoadingState(types.SET_MODIFIERS_ON_OPTIONS_REQUESTED));
    const numRequests = requestsInfo.length;
    dispatch(addRequestsLeftToLoad(numRequests));
    const responses = requestsInfo.map(({ actionName, path }) => {
      try {
        if (actionName === 'addResource') return ResourcesAPI[actionName](apiToken, option, path);
        if (actionName === 'deleteResource') return ResourcesAPI[actionName](apiToken, path);
      } catch (e) {
        dispatch(dataRetrievalFailure(e));
      }
    });
    const results = await Promise.allSettled(responses);
    const fulfilledPromises = results.filter(promiseResult => promiseResult.status === 'fulfilled');
    let successfulNewCustomizations = fulfilledPromises.map((promiseResult) => {
      const isDeleteResponse = get(promiseResult, 'value.resourcePath').length === 4;
      if (isDeleteResponse) return null;
      const customizationId = get(promiseResult, 'value.resourcePath[1]');
      return selectedCustomizations.find(idNameObject => idNameObject.id === customizationId);
    });

    Object.values(stayedSelectedsMap).forEach((idNameObj) => {
      successfulNewCustomizations.push(idNameObj);
    });
    const newCustomizationsValue = successfulNewCustomizations.filter(c => !!c);

    const newOption = Object.assign({}, option, { customizations: newCustomizationsValue });

    dispatch(removeRequestsLeftToLoad(numRequests));
    const alteredResourcePath = ['options', option.id];
    dispatch(updateResourceSuccess(newOption, alteredResourcePath));

    return newOption;
  };
}

/**
 * To keep performance higher,
 * this only runs one final update to a modifier in the Redux store
 * once all the requests come in.
 */
export function setExistingOptionsOnModifier({
  allOptionsMap,
  apiToken,
  originalModifierDetails,
  requestsInfo,
  stayedSelectedsMap,
}) {
  return async (dispatch) => {
    dispatch(updateLoadingState(types.SET_EXISTING_OPTIONS_ON_MODIFIER));
    const numRequests = requestsInfo.length;
    dispatch(addRequestsLeftToLoad(numRequests));
    const responses = requestsInfo.map(({ actionName, path, option }) => {
      try {
        if (actionName === 'addResource') return ResourcesAPI[actionName](apiToken, option, path);
        if (actionName === 'deleteResource') return ResourcesAPI[actionName](apiToken, path);
      } catch (e) {
        dispatch(dataRetrievalFailure(e));
      }
    });
    const results = await Promise.allSettled(responses);
    const fulfilledPromises = results.filter(promiseResult => promiseResult.status === 'fulfilled');
    let attachedOptions = fulfilledPromises.map((promiseResult) => {
      const isDeleteResponse = get(promiseResult, 'value.resourcePath').length === 4;
      if (isDeleteResponse) return null;
      const optionId = get(promiseResult, 'value.data.id');
      return allOptionsMap[optionId];
    });

    attachedOptions = attachedOptions.filter(option => !!option);

    Object.values(stayedSelectedsMap).forEach(option => attachedOptions.push(option));
    const { id, name } = originalModifierDetails;
    const currentModifierDisplayObj = { id, name };
    const optionsWithCurrentModifier = attachedOptions.map((option) => {
      const optionClone = cloneDeep(option);
      const { customizations } = optionClone;
      const currentCustomizationForDisplay = Array.isArray(customizations) && customizations.find(c => c.id === id);
      if (!currentCustomizationForDisplay) {
        customizations.push(currentModifierDisplayObj);
      }
      return optionClone;
    });

    const newCustomization = { ...originalModifierDetails, options: optionsWithCurrentModifier };
    const { id: customizationId } = originalModifierDetails;

    dispatch(removeRequestsLeftToLoad(numRequests));
    const resourcePath = ['customizations', customizationId];
    dispatch(updateResourceSuccess(newCustomization, resourcePath));
    return optionsWithCurrentModifier;
  };
}
